<template>
  <v-dialog
    :value="visible"
    max-width="600"
    persistent
    content-class="sc-modal sc-modal--md"
    @input="$emit('close')"
  >
    <v-card v-if="visible" flat class="sc-modal__card">
      <v-btn icon class="sc-modal__close" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Header -->
      <div class="sc-modal__header">
        <v-icon color="primary" class="mr-3">mdi-link-variant</v-icon>
        <div>
          <span class="sc-modal__title">Vincular Projeto GH</span>
          <span class="sc-modal__subtitle">
            Navegue até uma pasta/projeto no Graphic Hub e vincule-o a esta produção.
          </span>
        </div>
      </div>

      <!-- Body -->
      <div class="sc-modal__body">
        <!-- Current linked projects -->
        <div v-if="linkedProjects.length > 0" class="link-project__section">
          <span class="link-project__section-label">Projetos vinculados</span>
          <div class="link-project__chips">
            <span
              v-for="project in linkedProjects"
              :key="project.id_gh_projeto"
              class="link-project__chip"
            >
              <v-icon x-small class="link-project__chip-icon">mdi-folder</v-icon>
              <span class="link-project__chip-label">{{ project.dsc_nome_projeto_gh }}</span>
              <v-btn
                icon
                x-small
                class="link-project__chip-close"
                title="Desvincular"
                @click="handleUnlink(project)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </span>
          </div>
        </div>

        <!-- Browse to select a project -->
        <div class="link-project__section">
          <span class="link-project__section-label">Navegar pastas</span>
          <div class="link-project__list">
            <div
              v-if="currentPath.length > 0"
              class="link-project__row"
              @click="goBack"
            >
              <v-icon small class="link-project__row-icon">mdi-arrow-left</v-icon>
              <span class="link-project__row-title">Voltar</span>
            </div>
            <div
              v-for="folder in browseFolders"
              :key="folder.uuid"
              class="link-project__row"
              @click="browseInto(folder)"
            >
              <v-icon small class="link-project__row-icon">mdi-folder</v-icon>
              <span class="link-project__row-title">{{ folder.title }}</span>
              <v-btn
                icon
                small
                color="primary"
                class="link-project__row-link"
                title="Vincular esta pasta"
                @click.stop="linkFolder(folder)"
              >
                <v-icon small>mdi-link-plus</v-icon>
              </v-btn>
            </div>
            <v-progress-linear v-if="browseLoading" indeterminate class="link-project__progress" />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="sc-modal__footer">
        <v-btn text class="modal-btn-cancel" @click="$emit('close')">Fechar</v-btn>
      </div>
    </v-card>

    <ModalConfirmAction
      :visible="showUnlinkDialog"
      title="Desvincular projeto"
      :message="pendingUnlinkProject ? `Desvincular '${pendingUnlinkProject.dsc_nome_projeto_gh}'?` : ''"
      confirm-text="Desvincular"
      confirm-color="red"
      @confirm="confirmUnlink"
      @cancel="showUnlinkDialog = false"
    />
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { apiClient } from "@/services/api";

export default {
  name: "GloboGraphicsLinkProject",
  components: {
    ModalConfirmAction: () => import("@/components/Modal/ModalConfirmAction.vue"),
  },
  props: {
    visible: { type: Boolean, default: false },
    cod_producao: { type: Number, required: true },
  },
  data() {
    return {
      browseFolders: [],
      showUnlinkDialog: false,
      pendingUnlinkProject: null,
      browseLoading: false,
      currentPath: [],
    };
  },
  async created() {
    await this.loadRootFolders();
  },
  methods: {
    ...mapActions({
      linkProject: "globographics/linkProject",
      unlinkProject: "globographics/unlinkProject",
    }),
    async loadRootFolders() {
      this.browseLoading = true;
      try {
        const { data } = await apiClient.get("globographics/folders");
        this.browseFolders = data;
      } catch (error) {
        // Error handled by finally block
      } finally {
        this.browseLoading = false;
      }
    },
    async browseInto(folder) {
      this.browseLoading = true;
      this.currentPath.push({ uuid: folder.uuid, title: folder.title, prevFolders: [...this.browseFolders] });
      try {
        const { data } = await apiClient.get(`globographics/folders/${folder.uuid}`);
        this.browseFolders = data;
      } catch (error) {
        // Error handled by finally block
        this.currentPath.pop();
      } finally {
        this.browseLoading = false;
      }
    },
    goBack() {
      const prev = this.currentPath.pop();
      if (prev) {
        this.browseFolders = prev.prevFolders;
      }
    },
    async linkFolder(folder) {
      const breadcrumbPath = this.currentPath.map((p) => ({
        uuid: p.uuid,
        title: p.title,
      }));
      try {
        await this.linkProject({
          cod_producao: this.cod_producao,
          dsc_uuid_projeto_gh: folder.uuid,
          dsc_nome_projeto_gh: folder.title,
          dsc_caminho_gh: breadcrumbPath,
        });
        this.$toast.success('Projeto vinculado com sucesso');
      } catch (error) {
        const msg = error.response?.data?.error || 'Erro ao vincular projeto';
        this.$toast.error(msg);
      }
    },
    handleUnlink(project) {
      this.pendingUnlinkProject = project;
      this.showUnlinkDialog = true;
    },
    async confirmUnlink() {
      const project = this.pendingUnlinkProject;
      this.showUnlinkDialog = false;
      this.pendingUnlinkProject = null;
      try {
        await this.unlinkProject({
          idGhProjeto: project.id_gh_projeto,
          codProducao: this.cod_producao,
        });
        this.$toast.success('Projeto desvinculado');
      } catch (error) {
        const msg = error.response?.data?.error || 'Erro ao desvincular projeto';
        this.$toast.error(msg);
      }
    },
  },
  computed: {
    ...mapGetters({
      linkedProjects: "globographics/getterLinkedProjects",
    }),
  },
};
</script>

<style lang="scss" scoped>
// Structure comes from _modal.scss (sc-modal__*). These are the component's
// browse-specific extensions: section labels, linked-project chips, folder list.
.link-project__section {
  display: flex;
  flex-direction: column;
}
.link-project__section-label {
  margin-bottom: var(--sc-space-2);
  font-size: var(--sc-text-lg);
  font-weight: var(--sc-weight-semi);
  color: var(--v-text_secondary-base);
  text-transform: uppercase;
  letter-spacing: var(--sc-tracking-caps);
}

// Linked-project pills
.link-project__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sc-space-2);
}
.link-project__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-1);
  padding: var(--sc-space-1) var(--sc-space-1) var(--sc-space-1) var(--sc-space-3);
  background: var(--sc-surface-tint-strong);
  border: 1px solid var(--sc-border-subtle);
  border-radius: var(--sc-radius-pill);
  font-size: var(--sc-text-lg);
  color: var(--v-text_primary-base);
}
.link-project__chip-icon {
  color: var(--v-text_secondary-base) !important;
}
.link-project__chip-label {
  min-width: 0;
}
.link-project__chip-close {
  color: var(--v-text_secondary-base) !important;
}

// Folder browser
.link-project__list {
  max-height: 300px;
  overflow-y: auto;
  padding: var(--sc-space-1);
  border: 1px solid var(--sc-border-subtle);
  border-radius: var(--sc-radius-lg);
  background: var(--sc-surface-tint-subtle);
}
.link-project__row {
  display: flex;
  align-items: center;
  gap: var(--sc-space-3);
  padding: var(--sc-space-2) var(--sc-space-3);
  border-radius: var(--sc-radius-md);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint-hover);
  }
}
.link-project__row-icon {
  flex-shrink: 0;
  color: var(--v-text_secondary-base) !important;
}
.link-project__row-title {
  flex: 1;
  min-width: 0;
  font-size: var(--sc-text-lg);
  color: var(--v-text_primary-base);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.link-project__row-link {
  flex-shrink: 0;
}
.link-project__progress {
  margin-top: var(--sc-space-2);
}
</style>
